import { useEffect, useState, type FormEvent } from 'react';
import { firebaseIdToken, PROVIDER_LABEL, useAuthConfig, type FirebaseProviderId } from '../../lib/firebase';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ChevronDown, Clock, Download, History, Trash2 } from 'lucide-react';
import type { AuditEntry } from '@mailpilot/shared';
import { api, downloadFile } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate } from '../../lib/format';
import { Avatar } from '../../components/ui/Avatar';
import { Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Field, InlineError, Input, Modal, Notice, Segmented, Skeleton, useToast, type Tone } from '../../components/ui';
import { DangerCard, Rows, SettingRow } from '../../components/settings/SettingsPrimitives';
import { useMe, useUpdateSettings } from '../../hooks/useMe';
import { useClearLocalSession } from '../../components/settings/SecuritySignOut';
import { SettingsTitle } from './SettingsLayout';

const actorTone: Record<AuditEntry['actor'], Tone> = { user: 'primary', system: 'neutral', rule: 'warn', device: 'info' };

function RetentionCard() {
  const me = useMe();
  const update = useUpdateSettings();
  const retention = me.data?.settings.retentionDays ?? null;
  const [days, setDays] = useState<string>(retention ? String(retention) : '365');
  useEffect(() => {
    if (retention) setDays(String(retention));
  }, [retention]);

  const n = Math.round(Number(days));
  const invalid = !Number.isFinite(n) || n < 1;

  const commitDays = () => {
    if (invalid) return;
    if (n !== retention) update.mutate({ retentionDays: n });
  };

  return (
    <Card>
      <CardHeader icon={<Clock className="h-5 w-5" />} title="Data retention" description="How long MailPilot keeps imported mail, AI analyses and logs on the server. Mail in your provider account is never deleted by this setting." />
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        {me.isLoading ? (
          <Skeleton className="h-10 w-64" />
        ) : me.error ? (
          <ErrorState compact error={me.error} onRetry={() => void me.refetch()} />
        ) : (
          <Rows>
            <SettingRow
              label="Keep imported data"
              description={
                retention === null
                  ? 'Everything stays available for search, insights and AI context until you delete it.'
                  : `Local copies of messages, analyses and audit entries older than ${retention} days are removed daily.`
              }
              control={
                <Segmented
                  label="Retention"
                  value={retention === null ? 'forever' : 'days'}
                  onChange={(v) => update.mutate({ retentionDays: v === 'forever' ? null : invalid ? 365 : n })}
                  options={[
                    { value: 'forever', label: 'Forever' },
                    { value: 'days', label: 'For a set time' },
                  ]}
                />
              }
            />
            {retention !== null && (
              <div className="py-4 last:pb-0">
                <Field label="Delete after" hint="Applied daily on the server." error={invalid ? 'Enter a whole number of days (1 or more).' : undefined} className="max-w-xs">
                  {(id) => (
                    <div className="relative">
                      <Input
                        id={id}
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        onBlur={commitDays}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitDays();
                        }}
                        className="pr-14"
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[0.8125rem] text-muted">days</span>
                    </div>
                  )}
                </Field>
              </div>
            )}
          </Rows>
        )}
      </CardBody>
    </Card>
  );
}

function AuditRow({ e }: { e: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetails = e.details && Object.keys(e.details).length > 0;
  return (
    <li className="px-6 py-3.5">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-4">
        <time dateTime={e.createdAt} title={fullDate(e.createdAt)} className="shrink-0 text-xs tabular-nums text-muted sm:w-40 sm:pt-0.5">
          {fullDate(e.createdAt)}
        </time>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {e.actorName ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-ink" title={e.actorEmail ?? undefined}>
                <Avatar name={e.actorName} email={e.actorEmail ?? e.actorName} size={20} />
                {e.actorName}
              </span>
            ) : (
              <Badge tone={actorTone[e.actor]}>{e.actor === 'user' ? 'someone' : e.actor}</Badge>
            )}
            {e.actorName && e.actor !== 'user' && <Badge tone={actorTone[e.actor]}>{e.actor}</Badge>}
            <span className="break-all font-medium text-ink">{e.action}</span>
          </div>
          <p className="mt-0.5 break-all text-xs text-muted">
            {e.entityType}
            {e.entityId ? ` · ${e.entityId}` : ''}
          </p>
          {hasDetails && (
            <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary-ink hover:underline">
              <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
              {open ? 'Hide details' : 'Show details'}
            </button>
          )}
          {open && hasDetails && <pre className="mt-2 max-h-64 overflow-auto rounded-xl bg-sunken p-3 text-xs text-ink scroll-thin">{JSON.stringify(e.details, null, 2)}</pre>}
        </div>
      </div>
    </li>
  );
}

function AuditCard() {
  const audit = useInfiniteQuery({
    queryKey: qk.audit,
    queryFn: ({ pageParam }) => api('GET /api/audit', { query: pageParam ? { cursor: pageParam } : {} }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const entries = audit.data?.pages.flatMap((p) => p.entries) ?? [];
  return (
    <Card>
      <CardHeader
        icon={<History className="h-5 w-5" />}
        title="Audit log"
        description="A record of sends, rule actions, setting changes, sign-ins and data operations. Logs are redacted — they never contain message bodies or secrets."
      />
      {audit.isLoading ? (
        <CardBody className="space-y-4" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton className="h-3.5 w-40" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </CardBody>
      ) : audit.error && entries.length === 0 ? (
        <CardBody>
          <ErrorState compact error={audit.error} onRetry={() => void audit.refetch()} />
        </CardBody>
      ) : entries.length === 0 ? (
        <EmptyState icon={<History className="h-6 w-6" />} title="No activity yet" description="Actions taken by you, your rules and MailPilot will appear here." />
      ) : (
        <>
          <CardDivider className="mt-5" />
          <ul className="divide-y divide-line/70">
            {entries.map((e) => (
              <AuditRow key={e.id} e={e} />
            ))}
          </ul>
          {(audit.hasNextPage || audit.isFetchNextPageError) && (
            <>
              <CardDivider />
              <div className="flex flex-col items-center gap-3 px-6 py-4">
                {audit.isFetchNextPageError && <InlineError error={audit.error} />}
                {audit.hasNextPage && (
                  <Button size="sm" onClick={() => void audit.fetchNextPage()} loading={audit.isFetchingNextPage}>
                    Load more
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

function DeleteAccountCard() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [typed, setTyped] = useState('');
  const clearSession = useClearLocalSession();
  const me = useMe().data;
  const cfg = useAuthConfig().data;
  // Accounts created with Google (etc.) have no password: they confirm by signing in again.
  const providerOnly = !!me && !me.user.authProviders.includes('password');
  const reauthWith = (me?.user.authProviders ?? []).map((p) => p.replace(/\.com$/, '')).find((p): p is FirebaseProviderId => p in PROVIDER_LABEL);
  const del = useMutation({
    mutationFn: async () => {
      if (!providerOnly) return api('POST /api/data/delete-account', { body: { password } });
      if (!cfg?.firebase.web || !reauthWith) throw new Error('Sign-in with your provider is not available right now, so the account can’t be confirmed.');
      const idToken = await firebaseIdToken(cfg.firebase.web, reauthWith);
      if (!idToken) throw new Error('Sign in again to confirm it’s you.');
      return api('POST /api/data/delete-account', { body: { idToken } });
    },
    onSuccess: async () => {
      await clearSession('/signup');
    },
  });
  const canDelete = (providerOnly || password.length > 0) && typed.trim().toUpperCase() === 'DELETE';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canDelete) del.mutate();
  };
  const close = () => {
    if (del.isPending) return;
    setOpen(false);
    setPassword('');
    setTyped('');
    del.reset();
  };
  return (
    <DangerCard icon={<AlertTriangle className="h-5 w-5" aria-hidden />} title="Danger zone" description="Permanently delete your MailPilot account.">
      <CardDivider className="mt-5" />
      <CardBody className="!py-5">
        <SettingRow
          label="Delete account"
          description="Disconnects every mailbox, revokes all devices, and erases imported mail, drafts, rules, AI preferences, learned examples, signatures, contacts, library files and logs from MailPilot. Emails in your provider accounts are not deleted. This cannot be undone — export your data first if you may need it."
          control={
            <Button variant="danger" size="sm" icon={<Trash2 className="h-4 w-4" />} onClick={() => setOpen(true)}>
              Delete account…
            </Button>
          }
        />
      </CardBody>
      <Modal
        open={open}
        onClose={close}
        size="sm"
        title="Delete your account?"
        description="This permanently erases all your MailPilot data."
        footer={
          <>
            <Button onClick={close} disabled={del.isPending}>
              Cancel
            </Button>
            <Button variant="danger" type="submit" form="delete-account-form" loading={del.isPending} disabled={!canDelete}>
              Delete permanently
            </Button>
          </>
        }
      >
        <form id="delete-account-form" onSubmit={submit} className="space-y-5">
          {providerOnly ? (
            <Notice tone="info">You'll be asked to sign in with {reauthWith ? PROVIDER_LABEL[reauthWith] : 'your provider'} once more to confirm it's you.</Notice>
          ) : (
            <Field label="Your password" required>
              {(id) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-autofocus />}
            </Field>
          )}
          <Field label={<>Type <span className="font-mono font-semibold">DELETE</span> to confirm</>} required>
            {(id) => <Input id={id} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />}
          </Field>
          <InlineError error={del.error} />
        </form>
      </Modal>
    </DangerCard>
  );
}

export function DataSettings() {
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      await downloadFile('/api/data/export', 'mailpilot-export.json');
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };
  return (
    <div>
      <SettingsTitle title="Data & audit log" description="Control how long data is kept, download a copy of everything, review activity, or delete your account." />
      <div className="space-y-6">
        <RetentionCard />
        <Card>
          <CardHeader icon={<Download className="h-5 w-5" />} title="Export my data" description="A portable copy of your account you can keep or move elsewhere." />
          <CardDivider className="mt-5" />
          <CardBody className="!py-5">
            <SettingRow
              label="Download a JSON archive"
              description="Settings, mailboxes (without passwords or tokens), drafts, rules, AI preferences and learned examples, signatures, contacts and the audit log."
              control={
                <Button variant="primary" size="sm" icon={<Download className="h-4 w-4" />} loading={exporting} onClick={() => void exportData()}>
                  Export
                </Button>
              }
            />
          </CardBody>
        </Card>
        <AuditCard />
        <DeleteAccountCard />
      </div>
    </div>
  );
}
